import { z } from 'zod';

const PlanTypeItemSchema = z.object({
  id: z.number(),
  description: z.string(),
});

const CurrentTierSchema = z.object({
  ratePlanId: z.number(),
  currencyCode: z.string(),
  tier: z.number(),
  tierMin: z.number(),
  tierMax: z.number(),
  upperLimit: z.boolean(),
  price: z.number(),
  prorate: z.boolean(),
  pricingModel: z.string(),
  forceUpgrade: z.boolean(),
  pricingQualifier: z.string().nullable(),
  description: z.string().nullable(),
});

const UsageSchema = z.object({
  usageType: z.string(),
  used: z.number(),
  resetValue: z.number(),
  minValue: z.number().nullable(),
});

const ItemSchema = z.object({
  id: z.number(),
  description: z.string(),
});

const RatePlanSchema = z.object({
  planGroupId: z.number(),
  planId: z.number(),
  planType: z.string(),
  planTypeItem: PlanTypeItemSchema,
  currentTier: CurrentTierSchema,
  amountToInvoice: z.number(),
  usage: UsageSchema,
  ratePlanId: z.number(),
  ratePlanFrequency: z.string(),
  item: ItemSchema,
  invoiceDescription: z.string(),
  rateScheduleCode: z.string().nullable(),
  planNumber: z.string().nullable(),
  passPriceAsUnits: z.boolean(),
  promotionDiscAmts: z.record(z.number()).nullable(),
});

const AddOnUsageSchema = z
  .object({
    usageType: z.string(),
    used: z.number(),
    resetValue: z.number(),
    minValue: z.number().nullable(),
  })
  .nullable();

const AddOnRatingSchema = z.object({
  id: z.string(),
  name: z.string(),
  listPrice: z.number(),
  monthlyCost: z.number(),
  monthlyDiscount: z.number(),
  monthlyTax: z.number(),
  monthlyProRatedPrice: z.number().nullable(),
  monthlyProRatedTax: z.number().nullable(),
  proRatedPrice: z.number(),
  proRatedTax: z.number(),
  proRatedTotal: z.number(),
  totalTax: z.number(),
  totalPrice: z.number(),
  period: z.number(),
  termTotal: z.number(),
  termTax: z.number(),
  subTotal: z.number(),
  fullPrice: z.number(),
  summarizedTermTotal: z.number(),
  usage: AddOnUsageSchema,
});

const PromotionSchema = z.object({
  planId: z.number().nullable(), // null when promotion is invalid
  baasServiceId: z.string().nullable(),
  promotionCode: z.string(),
  promotionDescription: z.string(),
  active: z.boolean(),
  applied: z.boolean(),
  reasonResource: z.string(),
  reasonMessage: z.string(),
  proratedPromotionCredit: z.number().nullable(),
  promotionCredit: z.number().nullable(),
});

export const RatingResponseSchema = z.object({
  ratePlans: z.array(RatePlanSchema),
  addOnRating: z.array(AddOnRatingSchema).nullable(),
  newAddOnServices: z.array(z.string()).nullable(),
  existingAddOnServices: z.array(z.string()).nullable(),
  monthlyPrice: z.number(),
  monthlyTaxPrice: z.number(),
  paymentPeriod: z.number(),
  paymentPlanId: z.number(),
  prepayMonthlyDiscountAmount: z.number(),
  prepayTotalAmountSaved: z.number(),
  oneTimePrice: z.number(),
  oneTimeTaxPrice: z.number(),
  totalPrice: z.number(),
  totalTaxPrice: z.number(),
  proratedTotalPrice: z.number(),
  proratedTotalTaxPrice: z.number(),
  proratedPrepayDiscountAmount: z.number(),
  proratedPromotionDiscountAmount: z.number(),
  proratedPrice: z.number(),
  proratedTaxPrice: z.number(),
  startingBalance: z.number(),
  balanceDue: z.number(),
  amountAppliedFromBalance: z.number(),
  amountIncludedFromRemainingBalance: z.number(),
  amountToCharge: z.number(),
  finalBalance: z.number(),
  lastInvoiceAmount: z.number().nullable(),
  lastProratedDate: z.string().nullable(),
  promotionData: z.record(PromotionSchema).nullable(),
  paymentLessTax: z.number().nullable(),
  ignoreTaxExempt: z.boolean().nullable(),
  stripeQuoteId: z.string().nullable(),
});

export type RatingResponse = z.infer<typeof RatingResponseSchema>;
